import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import TenantSidebar from '../../components/tenant/TenantSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

export default function TenantSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const { register: regProfile, handleSubmit: handleProfile, reset: resetProfile, formState: { errors: profileErrors } } = useForm();
  const { register: regPw, handleSubmit: handlePw, reset: resetPw, watch, formState: { errors: pwErrors } } = useForm();

  useEffect(() => {
    api.get('/tenant/me').then(res => {
      const t = res.data.tenant;
      resetProfile({ first_name: t.first_name, last_name: t.last_name, phone: t.phone || '' });
    }).catch(() => toast.error('Could not load account'))
      .finally(() => setLoading(false));
  }, [resetProfile]);

  const onProfileSave = async (data) => {
    setSaving(true);
    try {
      await api.put('/tenant/me', data);
      toast.success('Profile updated successfully');
    } catch {
      toast.error('Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  const onPasswordChange = async ({ current_password, new_password }) => {
    setChangingPw(true);
    try {
      await api.post('/auth/tenant/change-password', { current_password, new_password });
      toast.success('Password changed successfully');
      resetPw();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not change password');
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="tenant" />
      <div className="dashboard-layout">
        <TenantSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Account Settings</div>
          <div className="page-sub">Manage your personal information and password.</div>

          <div style={{ maxWidth: 560 }}>
            {/* Profile */}
            <div className="card">
              <div className="card-header"><div className="card-title">Personal information</div></div>
              <div className="card-body">
                <form onSubmit={handleProfile(onProfileSave)}>
                  <div className="two-col">
                    <div className="form-group">
                      <label className="form-label">First name</label>
                      <input className={`form-input${profileErrors.first_name ? ' error' : ''}`}
                        {...regProfile('first_name', { required: true })} />
                      {profileErrors.first_name && <div className="form-error">Required</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last name</label>
                      <input className={`form-input${profileErrors.last_name ? ' error' : ''}`}
                        {...regProfile('last_name', { required: true })} />
                      {profileErrors.last_name && <div className="form-error">Required</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone number</label>
                    <input className="form-input" placeholder="(555) 000-0000" {...regProfile('phone')} />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save changes'}
                  </button>
                </form>
              </div>
            </div>

            {/* Password */}
            <div className="card">
              <div className="card-header"><div className="card-title">Change password</div></div>
              <div className="card-body">
                <form onSubmit={handlePw(onPasswordChange)}>
                  <div className="form-group">
                    <label className="form-label">Current password</label>
                    <input type="password" className={`form-input${pwErrors.current_password ? ' error' : ''}`}
                      {...regPw('current_password', { required: true })} />
                    {pwErrors.current_password && <div className="form-error">Required</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">New password</label>
                    <input type="password" className={`form-input${pwErrors.new_password ? ' error' : ''}`}
                      {...regPw('new_password', { required: true, minLength: 8 })} />
                    {pwErrors.new_password && <div className="form-error">Minimum 8 characters</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm new password</label>
                    <input type="password" className={`form-input${pwErrors.confirm ? ' error' : ''}`}
                      {...regPw('confirm', {
                        required: true,
                        validate: v => v === watch('new_password') || 'Passwords do not match'
                      })} />
                    {pwErrors.confirm && <div className="form-error">{pwErrors.confirm.message}</div>}
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={changingPw}>
                    {changingPw ? 'Updating...' : 'Change password'}
                  </button>
                </form>
              </div>
            </div>

            {/* Danger zone */}
            <div className="card" style={{ border: '1px solid #f5a0a0' }}>
              <div className="card-header" style={{ background: '#fff5f5' }}>
                <div className="card-title" style={{ color: 'var(--red)' }}>Danger zone</div>
              </div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: 'var(--text-sub)', marginBottom: 14 }}>
                  Cancelling your service will keep your account active until the end of the current billing period.
                  To cancel, go to <strong>My Plan</strong> and click "Cancel plan".
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-sub)' }}>
                  To permanently delete your account, please contact{' '}
                  <a href="mailto:support@affordabletelecom.com" style={{ color: 'var(--teal)' }}>support@affordabletelecom.com</a>.
                </p>
              </div>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
